import React, { useEffect, useState, useRef } from "react";
import SidebarItem from "./SidebarItem";
import { FaCrown, FaTrash, FaEllipsisV } from "react-icons/fa";
import "../css/SidebarItem.css";
import api from "../api";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedMenu, setRefreshSidebar } from "../redux/sidebarSlice";
import { setSessionId } from "../redux/sessionSlice";
import { triggerRefresh } from "../redux/refreshSlice"; // ✅ Import refresh trigger
import { setSelectedModel, setSelectedMode } from "../redux/modelSlice";
import ChatIcon from "../assets/UserIcons/NewChatIcon.svg";
import TrainIcon from "../assets/UserIcons/LearningSpace.svg";
import HistoryIcon from "../assets/UserIcons/KnowledgeRepositoryIcon.svg";
import FolderNav from "../assets/UserIcons/MyKnowledgeSpace.svg";
import onlineIcon from "../assets/UserIcons/OnlineMode.svg";
import offlineIcon from "../assets/UserIcons/OfflineModeWhite.svg";
import crossIcon from "../assets/UserIcons/Cancel.svg";
import deleteIcon from "../assets/UserIcons/DeleteIcon.svg";
import { setPrivacyMode } from "../redux/privacySlice";
import VelaKoda from "../assets/Images/LoginLogo.svg";
import VelaLogo from "../assets/Images/ZeroBoxLogo.svg";

const Sidebar = ({ expanded, toggleSidebar }) => {
  const [sessions, setSessions] = useState([]);
  const [documentSessions, setDocumentSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSessionsToDelete, setSelectedSessionsToDelete] = useState([]);

  const dropdownRef = useRef(null);
  const chatHistoryRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const dispatch = useDispatch();
  const refreshSidebar = useSelector((state) => state.sidebar.refreshSidebar);
  const selectedMenu = useSelector((state) => state.sidebar.selectedMenu);
  const isFullPrivacy = useSelector((state) => state.privacy.isFullPrivacy);
  // console.log("Full Privacy Mode:", isFullPrivacy);

  const handleMenuClick = (label) => {
    dispatch(setSelectedMenu(label));
  };

  useEffect(() => {
    // Set default selected menu based on URL path
    const pageLabelsMap = {
      "/user/new": "New Chat",
      "/user/notifications": "Notifications",
      "/user/profile": "Profile Settings",
      "/user/getHelpPage": "Get Help",
      "/user/learning-space":
        "Learning Space - Personalized area to explore training materials, tutorials, and guided learning content.",
      "/user/knowledgerepository-documents":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/knowledgerepository-weburls":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/knowledgerepository-videourls":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/knowledgerepository-deletedocs":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/texthistory":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/qahistory":
        "Knowledge Repository - Central storage for all documents and resources for you.",
      "/user/knowledgespace-saved":
        "My Knowledge Space - Your private workspace to save, view, and manage your selected learning resources.",
      "/user/knowledgespace-deleted":
        "My Knowledge Space - Your private workspace to save, view, and manage your selected learning resources.",
      "/user/premium-page":
        "Premium - Try new experiences with Premium features.",
    };

    const currentPath = pageLabelsMap[location.pathname] || "";

    // update redux only if changed
    if (selectedMenu !== currentPath) {
      dispatch(setSelectedMenu(currentPath));
    }
  }, [location.pathname, selectedMenu, dispatch]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchChatHistory = async () => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_URL}/chat/sessions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      let sessions = res.data.sessions || [];

      // ❗ Optional: keep only non-incognito
      // sessions = sessions.filter((session) => session.mode !== "incognito");

      // Set full list
      setSessions(sessions);

      // console.log("All Sessions Loaded:", sessions);
    } catch (err) {
      console.error("Failed to load chat sessions:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchChatHistory();
    }
  }, [token, isFullPrivacy]);

  // useEffect(() => {
  //   // ✅ Redirect to New Chat when switching to Full Privacy
  //   if (isFullPrivacy) {
  //     // Clear any session state
  //     dispatch(setSessionId(null));

  //     // Navigate to New Chat page
  //     navigate("/user/new", { replace: true, state: { sessionId: null } });
  //   }
  // }, [isFullPrivacy, dispatch, navigate]);

  useEffect(() => {
    if (refreshSidebar && token) {
      fetchChatHistory();
      dispatch(setRefreshSidebar(false));
    }
  }, [refreshSidebar, token, isFullPrivacy]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Close dropdown if clicked outside
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }

      // Reset selection if clicked outside chat history area
      if (
        chatHistoryRef.current &&
        !chatHistoryRef.current.contains(e.target)
      ) {
        setSelectMode(false);
        setSelectedSessions([]);
      }

      // ✅ Mobile-only sidebar close
      if (
        window.innerWidth < 768 && // 👈 Mobile screen check
        expanded &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded, toggleSidebar]);

  useEffect(() => {
    const stateSessionId = location.state?.sessionId || null;
    setActiveSessionId(stateSessionId);
  }, [location.state?.sessionId]);

  const toggleSelect = (type) => {
    if (type === "all") {
      const allSessionIds = sessions.map((s) => s.session_id);
      setSelectedSessions(allSessionIds); // ✅ Select everything
    } else {
      setSelectedSessions([]); // ✅ Deselect all
    }
    setSelectMode(true);
    setDropdownOpen(false);
  };

  const handleDeleteSelected = () => {
    if (selectedSessions.length === 0) {
      // Optional: show toast or alert for no selection
      return;
    }
    setSelectedSessionsToDelete(selectedSessions);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const normalIds = selectedSessionsToDelete.filter((id) =>
        sessions.some((s) => s.session_id === id)
      );

      // Delete normal sessions individually
      await Promise.all(
        normalIds.map((id) =>
          api.delete(`${process.env.REACT_APP_API_URL}/chat/history/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );

      // UI cleanup
      setSessions((prev) =>
        prev.filter((s) => !selectedSessionsToDelete.includes(s.session_id))
      );
      setSelectedSessions([]);
      setSelectMode(false);
      setSelectedSessionsToDelete([]);
      await fetchChatHistory();
      navigate("/user/new", { state: { sessionId: null } });
    } catch (err) {
      console.error("Failed to delete selected sessions:", err);
      // Optional: show error modal or toast
    }
  };

  const handleCheckboxChange = (sessionId) => {
    setSelectedSessions((prev) =>
      prev.includes(sessionId)
        ? prev.filter((id) => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const handleLogoClick = () => {
    navigate("/"); // navigate to login page
  };

  return (
    <div
      ref={sidebarRef}
      className={`sidebar d-flex flex-column h-100 ${
        expanded ? "expanded" : "collapsed"
      }`}
    >
      <div
        className={`logo-section ${
          expanded
            ? "d-flex justify-content-between align-items-center px-3"
            : "d-flex align-items-center justify-content-center gap-2 py-2"
        }`}
      >
        {expanded ? (
          <>
            <img
              src={VelaKoda}
              alt="Logo"
              className="logo"
              style={{
                width: "160px",
                height: "30px",
                objectFit: "contain",
                cursor: "pointer",
              }}
              onClick={handleLogoClick}
            />
            <button onClick={toggleSidebar} className="sidebar-toggler ms-2">
              <i className="bi bi-chevron-double-left" />
            </button>
          </>
        ) : (
          <>
            <img
              src={VelaLogo}
              alt="Bot Icon"
              className={`bot-icon w-50 ${expanded ? "expanded" : "collapsed"}`}
              style={{ cursoer: "pointer" }}
              onClick={handleLogoClick}
            />
            <button
              onClick={toggleSidebar}
              className={`sidebar-toggler ${
                expanded ? "expanded" : "collapsed"
              }`}
            >
              <i className="bi bi-chevron-double-right" />
            </button>
          </>
        )}
      </div>

      <div className="overflow-y-scroll menu-section d-flex flex-column justify-content-between">
        <div>
          <ul className="menu">
            <SidebarItem
              icon={ChatIcon}
              label="New Chat"
              to="/user/new"
              expanded={expanded}
              // onClick={() => setActiveSessionId(null)}
              // active={!activeSessionId}
              onClick={() => {
                handleMenuClick("New Chat");
                setActiveSessionId(null); // optional
                navigate(0);
              }}
              active={selectedMenu === "New Chat" && !activeSessionId}
            />
            <SidebarItem
              icon={TrainIcon}
              label="Learning Space"
              to="/user/learning-space"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("Learning Space");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                  "Learning Space - Personalized area to explore training materials, tutorials, and guided learning content." &&
                !activeSessionId
              }
            />

            <SidebarItem
              icon={HistoryIcon}
              label="Knowledge Repository"
              to="/user/knowledgerepository-documents"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("Knowledge Repository");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                  "Knowledge Repository - Central storage for all documents and resources for you." &&
                !activeSessionId
              }
            />

            <SidebarItem
              icon={FolderNav}
              label="My Knowledge Space"
              to="/user/knowledgespace-saved"
              expanded={expanded}
              onClick={() => {
                handleMenuClick("My Knowledge Space");
                setActiveSessionId(null);
              }}
              active={
                selectedMenu ===
                  "My Knowledge Space - Your private workspace to save, view, and manage your selected learning resources." &&
                !activeSessionId
              }
            />
          </ul>
        </div>

        <div className="d-flex flex-column ">
          {/* Chat History */}
          {expanded && (
            <div className="chat-history-wrapper m-1" ref={chatHistoryRef}>
              <div className="chat-history-title d-flex justify-content-between align-items-center">
                <p className="m-0">Chat History</p>
                <div
                  className="d-flex align-items-center position-relative"
                  ref={dropdownRef}
                >
                  {selectMode && (
                    <FaTrash
                      className="me-2"
                      style={{ cursor: "pointer" }}
                      onClick={handleDeleteSelected}
                      title="Delete selected"
                    />
                  )}
                  <FaEllipsisV
                    style={{ cursor: "pointer" }}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  />
                  {dropdownOpen && (
                    <div
                      className="dropdown-menu-session"
                      style={{ top: "25px", right: 0 }}
                    >
                      <div onClick={() => toggleSelect("all")}>Select All</div>
                      <div onClick={() => toggleSelect("custom")}>
                        Select Items
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="chat-history-list">
                {loading ? (
                  <div className="textwhite">Loading...</div>
                ) : (
                  sessions.map((session) => (
                    <div
                      key={session.session_id}
                      className={`chat-history-item position-relative custom-hover d-flex justify-content-between mb-1 align-items-center px-2 py-1 ${
                        activeSessionId === session.session_id ? "active" : ""
                      }`}
                    >
                      <div
                        className="d-flex align-items-center w-100"
                        onClick={() => {
                          if (
                            session.session_type?.toLowerCase() === "online"
                          ) {
                            dispatch(setPrivacyMode(false));
                          } else {
                            dispatch(setPrivacyMode(true));
                          }

                          navigate("/user/new", {
                            state: { sessionId: session.session_id },
                          });

                          dispatch(setSessionId(session.session_id));

                          if (session.model_id) {
                            dispatch(setSelectedModel(session.model_id));
                            // console.log("Selected Model:", session.model_id);
                          }

                          dispatch(triggerRefresh());
                        }}
                      >
                        {selectMode && (
                          <input
                            type="checkbox"
                            className="me-2 sidebar-check"
                            checked={selectedSessions.includes(
                              session.session_id
                            )}
                            onChange={() =>
                              handleCheckboxChange(session.session_id)
                            }
                          />
                        )}

                        {/* Online / Offline Icon */}
                        <img
                          src={
                            session.session_type?.toLowerCase() === "online"
                              ? onlineIcon
                              : offlineIcon
                          }
                          alt="mode-icon"
                          className={
                            session.session_type?.toLowerCase() === "online"
                              ? "me-2 chat_icon1" // Online → no custom class
                              : "me-2 chat_icon chat_history_img" // Offline → custom class
                          }
                        />

                        {/* Title with ellipsis */}
                        <div
                          style={{
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                          }}
                        >
                          {session.first_input?.slice(0, 30) || "New Chat"}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {showDeleteModal && selectedSessionsToDelete.length > 0 && (
            <div className="modal modal-backdrop-custom d-block" tabIndex="-1">
              <div className="modal-history modal-dialog-centered history-model">
                <div className="modal-content">
                  {/* Close Button */}
                  <button
                    type="button"
                    className="modal-close-btn position-absolute top-0 end-0 mt-2 me-2"
                    onClick={() => setShowDeleteModal(false)}
                    aria-label="Close"
                  >
                    <img
                      src={crossIcon}
                      className="icon-image cross-i"
                      alt="Close"
                    />
                  </button>

                  {/* Modal Body */}
                  <div className="text-center p-3">
                    <div
                      className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        borderRadius: "50%",
                        backgroundColor: "#f0f0f0",
                        // boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <img
                        src={deleteIcon} // ← your train icon import
                        alt="Training"
                        style={{ width: "40px", height: "40px" }}
                      />
                    </div>
                    <p className="fw-bold mt-3 mb-3 refresh-prompts">
                      Are you sure you want to delete the selected chats? This
                      action cannot be undone.
                    </p>
                  </div>

                  {/* Modal Footer */}
                  <div className="modal-footer justify-content-center gap-2">
                    <button
                      type="button"
                      className="px-5 rounded cancle-btn border-none py-2 fw-bold"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      type="button"
                      className="px-5 rounded custom-btn border-none py-2 fw-bold history-btn"
                      onClick={async () => {
                        await handleConfirmDelete();
                        setShowDeleteModal(false);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Premium Section */}
      <div className="p-2 mt-auto text-center">
        <div className={`premium-transition ${expanded ? "show" : "hide"}`}>
          {expanded ? (
            // <div className="premium-card-container">
            //   <div className="premium-card-header">
            //     <div className="premium-icon-box">
            //       <FaCrown className="" size={18} />
            //     </div>
            //     <div>
            //       <div className="premium-title refresh-prompts">
            //         Get Premium
            //       </div>
            //       <div className="premium-description">
            //         Try new experiences with premium features
            //       </div>
            //     </div>
            //   </div>
            //   <button
            //     className="premium-upgrade-btn"
            //     onClick={() => navigate("/premium-page")}
            //   >
            //     Upgrade Plan
            //   </button>
            //   <span
            //     className="text-center refresh-prompts"
            //     style={{ fontSize: "small" }}
            //   >
            //     v1.1.9
            //   </span>
            // </div>

            <span
                className="text-center"
                style={{ fontSize: "small" }}
              >
                v1.0.0
              </span>

          ) : (
            <div
              className="d-flex justify-content-center"
              // onClick={() => navigate("/premium-page")} // 👈 logic written inline here
              style={{ cursor: "pointer" }}
            >
              {/* <FaCrown
                className="textwhite"
                size={24}
                title="Upgrade to Premium"
              /> */}

               <span
                className="text-center"
                style={{ fontSize: "small" }}
              >
                v1
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
